import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Tabs from '@mui/material/Tabs';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { TranslationKey } from '~/shared/internationalization/translation';

import type { GlobalPermissions } from '../../../data-contract/global-search';
import { ContentHeader } from '../../../design/components/containers/ContentWrapper';
import { TabWithBadge } from '../../../design/components/tabs/TabWithBadge';
import { useNavigationTabs } from '../../common/use-navigation-tab';
import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import { useTalentDirectoryCounts } from '../../query-hooks/talent-directory/count';
import { EmployeesTable } from './EmployeesTable';
import { JobsTable } from './JobsTable';
import { LearningOpportunitiesTable } from './LearningOpportunitiesTable';
import { SkillsTable } from './SkillsTable';
import type { TabParam } from './use-talent-directory-params';
import { useTalentDirectoryParams } from './use-talent-directory-params';

type TabConfig = {
    readonly ariaLabel: string;
    readonly key: TabParam;
    readonly permission: keyof GlobalPermissions;
    readonly translationKey: TranslationKey;
};

const tabs: readonly TabConfig[] = [
    {
        ariaLabel: 'Skills',
        key: 'skills',
        permission: 'canDirectorySkills',
        translationKey: 'pages.global-search.navigation.skills',
    },
    {
        ariaLabel: 'Employees',
        key: 'employees',
        permission: 'canDirectoryUsers',
        translationKey: 'pages.global-search.navigation.employees',
    },
    {
        ariaLabel: 'Jobs',
        key: 'jobs',
        permission: 'canDirectoryJobs',
        translationKey: 'pages.global-search.navigation.job-openings',
    },
    {
        ariaLabel: 'LearningOpportunities',
        key: 'learningOpportunities',
        permission: 'canDirectoryLearningOpportunities',
        translationKey: 'pages.global-search.navigation.learning-opportunities',
    },
];

export const TalentTabs: FunctionComponent = () => {
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();
    const availableTabs = isSuccess ? tabs.filter(({ permission }) => globalPermissions?.[permission]) : [];
    const availableTabKeys = availableTabs.map(({ key }) => key);

    const { currentTab, currentTabIndex } = useNavigationTabs(availableTabs);
    const currentTabKey = currentTab.get('key').unwrapOr(availableTabs[0]?.key);

    const { getSearchParamsForTab } = useTalentDirectoryParams();
    const { translate } = useContext(ApplicationContext);
    const globalSearchEntitiesCounter: Record<TabConfig['key'], number> = useTalentDirectoryCounts(availableTabKeys);

    return (
        <TableContainer
            aria-label="talent directory tabs"
            className="talent-tabs"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'stretch',
                paddingBottom: 3,
            }}
        >
            <ContentHeader>
                <Tabs aria-label="table" value={currentTabIndex}>
                    {availableTabs.map(({ ariaLabel, key, translationKey }) => (
                        <TabWithBadge
                            key={key}
                            aria-label={ariaLabel}
                            badgeLabel={globalSearchEntitiesCounter[key].toString()}
                            label={translate(translationKey)}
                            replace={false}
                            to={getSearchParamsForTab(key)}
                        />
                    ))}
                </Tabs>
            </ContentHeader>
            <Grid>
                {currentTabKey === 'skills' && availableTabKeys.includes(currentTabKey) && <SkillsTable />}
                {currentTabKey === 'employees' && availableTabKeys.includes(currentTabKey) && <EmployeesTable />}
                {currentTabKey === 'jobs' && availableTabKeys.includes(currentTabKey) && <JobsTable />}
                {currentTabKey === 'learningOpportunities' && availableTabKeys.includes(currentTabKey) && (
                    <LearningOpportunitiesTable />
                )}
            </Grid>
        </TableContainer>
    );
};
